import { styled } from '@material-ui/core';
import { useIO, useLinked } from '@type-r/react';
import { AgGridReact } from 'ag-grid-react';
import { globalStore } from 'api/store';
import { CollapsableSection, Column, Columns, Flex, FormLabel, FormSelect, Input, Loading, Option } from 'lqx-react';
import * as React from 'react';
import { useCallback, useRef } from 'react';
import NumberFormat from 'react-number-format';
import { hasRole } from 'src/app/services/can';
import { Company, getCompanies, getCompaniesAsCommonParticipants } from '../../api/company';
import { getBuyersAsCommonParticipants, getSuppliersAsCommonParticipants } from '../../api/companyParticipant';
import { Component } from '../../api/component';
import { Invoice } from '../../api/invoice';
import { ManualInvoice } from '../../api/manualInvoice';
import { NumberWithDecimalEditor, NumberWithoutDecimalEditor, SelectUnitEditor } from '../../components/ag-grid-cell-formating';
import { AgGridReactContainer, StyledAddCircle, StyledRemoveCircle } from '../../components/ag-grid-styles';
import { DigiDateWrapper } from '../../components/date-input';
import { AdditionalFields, AssetColumn, AssetLabel, numberInputStyles } from "../../components/number-input";
import { DigiAsyncSelect, DigiCurrencySelect, DigitizationSelect } from '../../components/select';
import { DigiSelectAsync } from '../../components/select-async';
import { DigiDateInput, ErrorHighligter, asTimelessLinkedDate } from '../../shared';
import { InvoiceComponentTableColumns } from './invoice-component-table-columns';

export const InvoiceForm = ({ invoice }: { invoice: Invoice | ManualInvoice }) => {
    const classes = numberInputStyles();
    const defaultColDef = {
        editable: true,
        filter: false,
        resizable: true,
        minWidth: 150
    };
    const gridApi = useRef(null);
    const onGridReady = (params: { api: any; }) => {
        gridApi.current = params.api;
    };
    const onAddRow = useCallback(() => {
        invoice.components.unshift(new Component());
        gridApi.current!.setRowData(invoice.components.models);
        gridApi.current!.setFocusedCell(0, 'number');
        gridApi.current!.startEditingCell({ rowIndex: 0, colKey: 'number' });
    }, [])

    const onRemoveRow = useCallback(() => {
        const selectedData = gridApi.current!.getSelectedRows();
        invoice.components.remove(selectedData);
        gridApi.current.updateRowData({ remove: selectedData });
    }, []);

    const isReady = useIO(
        async () => {
            if (invoice.dataHolderId) {
                $dataHolder.set((await getCompanies(null, invoice.dataHolderId)).items[0]);
            }
            await globalStore.governingLawsLookup.fetch();
        }, []);

    const $dataHolder = useLinked<Company>(new Company())
        .onChange(s => {
            if (s && s.id) {
                invoice.dataHolderId = Number(s.id);
            }
        });

    return isReady ? <>
        <Flex row>
            <AssetColumn>
                <AssetLabel>Supplier *</AssetLabel>
                <ErrorHighligter error={invoice.errors['supplier']}>
                    <DigiAsyncSelect
                        aria-label="Supplier"
                        data-qa-id={`supplier`}
                        getItems={getSuppliersAsCommonParticipants}
                        $value={invoice.$.supplier}
                        isCreater={hasRole('AssetFacilitator')}
                    />
                </ErrorHighligter>
            </AssetColumn>
        </Flex>
        <Flex row>
            <AssetColumn>
                <AssetLabel>Buyer *</AssetLabel>
                <ErrorHighligter error={invoice.errors['buyer']}>
                    <DigiAsyncSelect
                        aria-label="Buyer"
                        data-qa-id={`buyer`}
                        getItems={getBuyersAsCommonParticipants}
                        $value={invoice.$.buyer}
                        isCreater={hasRole('AssetFacilitator')}
                    />
                </ErrorHighligter>
            </AssetColumn>
        </Flex>
        <Flex row>
            <AssetColumn>
                <AssetLabel>Governing Law</AssetLabel>
                <ErrorHighligter error={invoice.errors['governingLaw'] || invoice.warnings['governingLaw']}>
                    <DigitizationSelect
                        aria-label="Governing Law"
                        isMulti={false}
                        data-qa-id={`governingLaw`}
                        options={globalStore.governingLawsLookup.items.models}
                        $value={invoice.$.governingLaw}
                    />
                </ErrorHighligter>
            </AssetColumn>
        </Flex>
        <Columns>
            <AssetColumn>
                <AssetLabel>Invoice# *</AssetLabel>
                <ErrorHighligter error={invoice.errors['number']}>
                    <Input data-qa-id="invoice number" $value={invoice.$.number} aria-label="Invoice Number" />
                </ErrorHighligter>
            </AssetColumn>
            {hasRole('Administrator') &&
                <AssetColumn>
                    <AssetLabel>Data Owner</AssetLabel>
                    <DigiSelectAsync
                        data-qa-id={`data owner`}
                        getItems={getCompanies}
                        $value={$dataHolder}
                    />
                </AssetColumn>
            }
            <AssetColumn>
                <AssetLabel>Supplier External ID</AssetLabel>
                <ErrorHighligter error={invoice.errors['supplier.ExternalId']}>
                    <Input data-qa-id="supplier external id" $value={invoice.supplier.$.externalId} aria-label="Supplier External Id" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Buyer External ID</AssetLabel>
                <ErrorHighligter error={invoice.errors['buyer.ExternalId']}>
                    <Input data-qa-id="buyer extrenal id" $value={invoice.buyer.$.externalId} aria-label="Buyer External Id" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Face Value *</AssetLabel>
                <ErrorHighligter error={invoice.errors['faceValue']}>
                    <NumberFormat aria-label="Face Value" data-qa-id="face value" value={invoice.faceValue} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.$.faceValue.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Currency *</AssetLabel>
                <ErrorHighligter error={invoice.errors['currency']}>
                    <DigiCurrencySelect
                        aria-label="Currency"
                        isMulti={false}
                        options={globalStore.currenciesLookup.items.models}
                        $value={invoice.$.currency}
                    />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Invoice Date *</AssetLabel>
                <ErrorHighligterStyled error={invoice.errors['invoiceDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="invoice date" date $value={asTimelessLinkedDate(invoice.$.invoiceDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Invoice Due Date *</AssetLabel>
                <ErrorHighligterStyled error={invoice.errors['invoiceDueDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="invoice due date" date $value={asTimelessLinkedDate(invoice.$.invoiceDueDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Anticipated Payment Date</AssetLabel>
                <ErrorHighligterStyled error={invoice.errors['anticipatedPaymentDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="anticipated payment date" date $value={asTimelessLinkedDate(invoice.$.anticipatedPaymentDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Anticipated Settlement Date</AssetLabel>
                <ErrorHighligterStyled error={invoice.errors['anticipatedSettlementDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="anticipated settlement date" date $value={asTimelessLinkedDate(invoice.$.anticipatedSettlementDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Effective Date</AssetLabel>
                <ErrorHighligterStyled error={invoice.errors['effectiveDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="effective date" date $value={asTimelessLinkedDate(invoice.$.effectiveDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </AssetColumn>
        </Columns>
        <Columns>
            <AdditionalFieldsColumn>
                <AdditionalFields>
                    <CollapsableSection title="Additional Fields" noMargins>
                        <Columns>
                            <AssetColumn>
                                <AssetLabel>Adjustment Amt</AssetLabel>
                                <ErrorHighligter error={invoice.errors['adjustmentAmount']}>
                                    <NumberFormat aria-label="Adjustment Amount" value={invoice.adjustmentAmount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.$.adjustmentAmount.set(floatValue) }} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Outstanding Amount Seller</AssetLabel>
                                <ErrorHighligter error={invoice.errors['outstandingAmountSeller']}>
                                    <NumberFormat aria-label="Outstanding Amount" value={invoice.outstandingAmountSeller} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.$.outstandingAmountSeller.set(floatValue) }} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Adjusted Face Value</AssetLabel>
                                <ErrorHighligter error={invoice.errors['adjustedFaceValue']}>
                                    <Input aria-label="Adjusted Face Value" data-qa-id="adjustment face value" number $value={invoice.$.adjustedFaceValue} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Purchase Order Number</AssetLabel>
                                <ErrorHighligter error={invoice.errors['purchaseOrder.Number']}>
                                    <Input aria-label="Purchase Order Number" $value={invoice.purchaseOrder.$.number} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Relationship Reference Number</AssetLabel>
                                <ErrorHighligter error={invoice.errors['relationshipIdentifier']}>
                                    <Input aria-label="Relationship Reference Number" data-qa-id="relationship reference number" $value={invoice.$.relationshipIdentifier} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Funding Date</AssetLabel>
                                <ErrorHighligterStyled error={invoice.errors['fundingDate']}>
                                    <DigiDateWrapper>
                                        <DigiDateInput aria-label="Funding Date" data-qa-id="funding date" date $value={asTimelessLinkedDate(invoice.$.fundingDate)} />
                                    </DigiDateWrapper>
                                </ErrorHighligterStyled>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Buyer DUNS</AssetLabel>
                                <ErrorHighligter error={invoice.errors['duns']}>
                                    <Input aria-label="Buyer DUNS" data-qa-id="buyers duns" $value={invoice.buyer.$.duns} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Insured Party Buyer Reference Number</AssetLabel>
                                <ErrorHighligter error={invoice.errors['buyerCustomIdentifierIP']}>
                                    <Input aria-label="Insured Party Buyer Reference Number" data-qa-id="insured party buyer ref number" $value={invoice.buyer.customIdentifiers.models[0].$.value} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Insurance Carrier Buyer Reference Number</AssetLabel>
                                <ErrorHighligter error={invoice.errors['buyerCustomIdentifierIC']}>
                                    <Input aria-label="Insurance Carrier Buyer Reference Number" data-qa-id="insurance carrier buyer ref number" $value={invoice.buyer.customIdentifiers.models[1].$.value} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Alternate ID</AssetLabel>
                                <ErrorHighligter error={invoice.errors['alternateId']}>
                                    <Input aria-label="AlternateId" data-qa-id="alternate id" $value={invoice.$.alternateId} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Notification Number</AssetLabel>
                                <ErrorHighligter error={invoice.errors['notificationNumber']}>
                                    <Input aria-label="Notification Number" data-qa-id="notification number" $value={invoice.$.notificationNumber} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Ref 1</AssetLabel>
                                <ErrorHighligter error={invoice.errors['ref1']}>
                                    <Input aria-label="Ref 1" data-qa-id="ref 1" $value={invoice.$.ref1} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Ref 2</AssetLabel>
                                <ErrorHighligter error={invoice.errors['ref2']}>
                                    <Input aria-label="Ref 2" data-qa-id="ref 2" $value={invoice.$.ref2} />
                                </ErrorHighligter>
                            </AssetColumn>
                            <AssetColumn>
                                <AssetLabel>Ref 3</AssetLabel>
                                <ErrorHighligter error={invoice.errors['ref3']}>
                                    <Input aria-label="Ref 3" data-qa-id="ref 3" $value={invoice.$.ref3} />
                                </ErrorHighligter>
                            </AssetColumn>
                        </Columns>
                    </CollapsableSection>
                </AdditionalFields>
            </AdditionalFieldsColumn>
        </Columns>

        <Columns>
            <AdditionalFieldsColumn>
                <AdditionalFields>
                    {invoice.hasProposedMonetization && <MonetizationSection invoice={invoice} expanded={true} />}
                    {!invoice.hasProposedMonetization && <MonetizationSection invoice={invoice} expanded={false} />}
                </AdditionalFields>
            </AdditionalFieldsColumn>
        </Columns>

        <CollapsableSection title="Components" noMargins notExpanded={true}>
            <Section row>
                <AddNewLine row>
                    <StyledAddCircle onClick={onAddRow}></StyledAddCircle>
                    <FormLabel>Add New Line</FormLabel>
                </AddNewLine>
                <RemoveLine row>
                    <StyledRemoveCircle onClick={onRemoveRow}></StyledRemoveCircle>
                    <FormLabel>Remove Line</FormLabel>
                </RemoveLine>
            </Section>
            <AgGridReactContainer className={`ag-theme-balham`}>
                <AgGridReact
                    onGridReady={onGridReady}
                    columnDefs={InvoiceComponentTableColumns}
                    frameworkComponents={{ NumberWithoutDecimalEditor, NumberWithDecimalEditor, SelectUnitEditor }}
                    rowSelection='multiple'
                    defaultColDef={defaultColDef}
                    overlayNoRowsTemplate={"No Items"}
                    rowData={invoice.components as any}>
                </AgGridReact>
            </AgGridReactContainer>
        </CollapsableSection>
        <br />
    </> : <Loading />;
};

const MonetizationSection = ({ invoice, expanded }: { invoice: Invoice | ManualInvoice, expanded: boolean }) => {
    const classes = numberInputStyles();
    const defaultValue = (value: any) => {
        return value ? value : '';
    };
    return <CollapsableSection title="Monetization Fields" noMargins notExpanded={!expanded}>
        <Columns>
            {!hasRole('AssetFacilitator') &&
                <AssetColumn>
                    <AssetLabel>Funder</AssetLabel>
                    <ErrorHighligter error={invoice.errors['proposedMonetization.Funder']}>
                        <DigiSelectAsync
                            data-qa-id={"funder"}
                            getItems={getCompaniesAsCommonParticipants}
                            $value={invoice.proposedMonetization.$.funder}
                        />
                    </ErrorHighligter>
                </AssetColumn>
            }
            <AssetColumn>
                <AssetLabel>Net Face Value</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.NetFaceValue']}>
                    <NumberFormat aria-label="Net Face Value" data-qa-id="netFaceValue" value={defaultValue(invoice.proposedMonetization.netFaceValue)} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.netFaceValue.set(floatValue ? floatValue : null) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Tenor</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.Tenor']}>
                    <Input aria-label="Tenor" data-qa-id="tenor" number $value={invoice.proposedMonetization.$.tenor} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Base Rate (decimal)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.BaseRate']}>
                    <NumberFormat aria-label="Base Rate (decimal)" data-qa-id="baseRate" value={defaultValue(invoice.proposedMonetization.baseRate)} className={classes.numericInput} decimalScale={10} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.baseRate.set(floatValue ? floatValue : null) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Base Rate (%)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.BaseRatePercentage']}>
                    <NumberFormat aria-label="Base Rate (%)"data-qa-id="baseRatePercentage" suffix='%' value={defaultValue(invoice.proposedMonetization.baseRatePercentage)} className={classes.numericInput} decimalScale={10} allowNegative={false} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.baseRatePercentage.set(floatValue ? floatValue : null) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Base Rate Amount</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.BaseRateAmount']}>
                    <NumberFormat aria-label="Base Rate Amount" data-qa-id="baseRateAmount" value={defaultValue(invoice.proposedMonetization.baseRateAmount)} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.baseRateAmount.set(floatValue ? floatValue : null) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Margin Rate (decimal)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.MarginRate']}>
                    <NumberFormat aria-label="Margin Rate (decimal)" data-qa-id="marginRate" value={defaultValue(invoice.proposedMonetization.marginRate)} className={classes.numericInput} decimalScale={10} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.marginRate.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Margin Rate  (%)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.MarginRatePercentage']}>
                    <NumberFormat aria-label="Margin Rate (%)" data-qa-id="marginRatePercentage" suffix='%' value={defaultValue(invoice.proposedMonetization.marginRatePercentage)} className={classes.numericInput} decimalScale={10} allowNegative={false} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.marginRatePercentage.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Margin Rate Amount</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.MarginRateAmount']}>
                    <NumberFormat aria-label="Margin Rate Amount" data-qa-id="marginRateAmount" value={defaultValue(invoice.proposedMonetization.marginRateAmount)} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.marginRateAmount.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Total Rate  (decimal)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.TotalRate']}>
                    <NumberFormat aria-label="Total Rate (decimal)"data-qa-id="totalRate" value={defaultValue(invoice.proposedMonetization.totalRate)} className={classes.numericInput} decimalScale={10} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.totalRate.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Total Rate  (%)</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.TotalRatePercentage']}>
                    <NumberFormat aria-label="Total Rate (%)" data-qa-id="totalRatePercentage" suffix='%' value={defaultValue(invoice.proposedMonetization.totalRatePercentage)} className={classes.numericInput} decimalScale={10} allowNegative={false} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.totalRatePercentage.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Total Rate Amount</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.TotalRateAmount']}>
                    <NumberFormat aria-label="Margin Rate Amount" data-qa-id="totalRateAmount" value={defaultValue(invoice.proposedMonetization.totalRateAmount)} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.totalRateAmount.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Purchase Price</AssetLabel>
                <ErrorHighligter error={invoice.errors['proposedMonetization.PurchasePrice']}>
                    <NumberFormat aria-label="Purchase Price" data-qa-id="purchasePrice" value={defaultValue(invoice.proposedMonetization.purchasePrice)} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; invoice.proposedMonetization.$.purchasePrice.set(floatValue) }} />
                </ErrorHighligter>
            </AssetColumn>
        </Columns>
    </CollapsableSection>
}

const AdditionalFieldsColumn = styled(Column)({
    margin: 0
});

const StatusSelect = styled(FormSelect)({
    width: '50px'
});

const ErrorHighligterStyled = styled(ErrorHighligter)({
    boxSizing: 'border-box',
    width: '100%'
})

const Section = styled(Flex)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: '0 0 5px 0'
})

const AddNewLine = styled(Flex)({
    width: '125px'
})

const RemoveLine = styled(Flex)({
    width: '116px'
})
